import { useEffect, useState, useRef, useCallback } from 'react';
import {
  fetchCorrespondents,
  fetchThreads,
  fetchMessages,
  sendMessage,
  createThread,
  fetchAvailability,
  uploadAttachment,
} from '../services/api';

interface Thread {
  id: string;
  recipient_type?: string;
  recipient_label?: string;
  status: string;
  last_message_preview?: string;
  last_message_at?: string;
  unread_count_hotel?: number;
  created_at: string;
}

interface Message {
  id: string;
  sender_type: string;
  content: string;
  has_attachments?: boolean;
  created_at: string;
}

function timeAgo(ts: string) {
  const diff = Date.now() - new Date(ts).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "À l'instant";
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(diff / 3600000);
  if (hrs < 24) return `${hrs}h`;
  return new Date(ts).toLocaleDateString('fr-FR');
}

function generateRoomId(): string {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let id = 'fpt-';
  for (let i = 0; i < 8; i++) id += chars[Math.floor(Math.random() * chars.length)];
  return id;
}

export default function MessagingPage() {
  const [threads, setThreads] = useState<Thread[]>([]);
  const [selectedThread, setSelectedThread] = useState<Thread | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMsg, setNewMsg] = useState('');
  const [sending, setSending] = useState(false);
  const [correspondents, setCorrespondents] = useState<any[]>([]);
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [loading, setLoading] = useState(true);
  const [msgLoading, setMsgLoading] = useState(false);
  const [availability, setAvailability] = useState<any>(null);
  const [attachFile, setAttachFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    Promise.all([fetchThreads(), fetchCorrespondents(), fetchAvailability()])
      .then(([t, c, a]) => { setThreads(t); setCorrespondents(c); setAvailability(a); })
      .finally(() => setLoading(false));
  }, []);

  // Auto-refresh messages every 10s when a thread is selected
  useEffect(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (!selectedThread) return;
    pollRef.current = setInterval(async () => {
      const msgs = await fetchMessages(selectedThread.id);
      setMessages(msgs);
      const updated = await fetchThreads();
      setThreads(updated);
    }, 10000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [selectedThread?.id]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const selectThread = useCallback(async (t: Thread) => {
    setSelectedThread(t);
    setMsgLoading(true);
    const msgs = await fetchMessages(t.id);
    setMessages(msgs);
    setMsgLoading(false);
  }, []);

  const handleSend = async () => {
    if ((!newMsg.trim() && !attachFile) || !selectedThread || sending) return;
    setSending(true);
    try {
      const msg = await sendMessage(selectedThread.id, newMsg.trim() || '📎 Pièce jointe');
      // Upload attachment if present
      if (attachFile && msg?.id) {
        await uploadAttachment(msg.id, attachFile).catch(() => {});
        setAttachFile(null);
      }
      setNewMsg('');
      const msgs = await fetchMessages(selectedThread.id);
      setMessages(msgs);
      const updated = await fetchThreads();
      setThreads(updated);
    } catch { /* silent */ }
    setSending(false);
  };

  const handleNewThread = async (recipientType: string) => {
    setShowNewDialog(false);
    try {
      const thread = await createThread(recipientType);
      const t = thread?.data || thread;
      const updated = await fetchThreads();
      setThreads(updated);
      if (t?.id) {
        const found = updated.find((u: Thread) => u.id === t.id);
        if (found) selectThread(found);
      }
    } catch { /* silent */ }
  };

  const startVideoCall = async () => {
    if (!selectedThread) return;
    const room = generateRoomId();
    const link = `https://meet.jit.si/${room}`;
    await sendMessage(selectedThread.id, `📹 Vidéoconférence démarrée — Rejoindre: ${link}`);
    const msgs = await fetchMessages(selectedThread.id);
    setMessages(msgs);
    const updated = await fetchThreads();
    setThreads(updated);
    window.open(link, '_blank', 'noopener');
  };

  const onlineCount = availability?.available_count || 0;

  return (
    <div style={{ display: 'flex', height: 'calc(100vh - 120px)', gap: 0 }}>
      {/* Thread list */}
      <div style={{ width: 320, borderRight: '1px solid var(--border, #e5e7eb)', display: 'flex', flexDirection: 'column', background: 'white' }}>
        <div style={{ padding: 16, borderBottom: '1px solid var(--border, #e5e7eb)' }}>
          <h3 style={{ margin: 0 }}>Messages</h3>
          <div style={{
            marginTop: 8, padding: '8px 12px', borderRadius: 8,
            background: onlineCount > 0 ? '#f0fdf4' : '#fffbeb',
            border: `1px solid ${onlineCount > 0 ? '#bbf7d0' : '#fde68a'}`,
            fontSize: 13
          }}>
            <span style={{
              display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
              background: onlineCount > 0 ? '#22c55e' : '#f59e0b', marginRight: 6
            }} />
            {onlineCount > 0 ? `${onlineCount} agent(s) en ligne` : 'Aucun agent en ligne'}
          </div>
          <button className="btn primary" style={{ width: '100%', marginTop: 12 }} onClick={() => setShowNewDialog(true)}>
            + Nouvelle conversation
          </button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto' }}>
          {loading ? <div style={{ padding: 16, color: '#9ca3af' }}>Chargement...</div> :
           threads.length === 0 ? <div style={{ padding: 16, color: '#9ca3af', textAlign: 'center' }}>Aucune conversation</div> :
           threads.map(t => (
            <div
              key={t.id}
              onClick={() => selectThread(t)}
              style={{
                padding: '12px 16px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6',
                background: selectedThread?.id === t.id ? '#eff6ff' : 'white',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <strong style={{ fontSize: 13 }}>{t.recipient_label || t.recipient_type || 'Conversation'}</strong>
                <span style={{ fontSize: 11, color: '#9ca3af' }}>{t.last_message_at ? timeAgo(t.last_message_at) : ''}</span>
              </div>
              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {t.last_message_preview || 'Pas de message'}
              </div>
              <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                <span className={`badge ${t.status === 'open' ? 'blue' : t.status === 'resolved' ? 'green' : 'gray'}`} style={{ fontSize: 10 }}>
                  {t.status}
                </span>
                {(t.unread_count_hotel || 0) > 0 && (
                  <span style={{ background: '#ef4444', color: 'white', borderRadius: 10, padding: '1px 6px', fontSize: 10 }}>
                    {t.unread_count_hotel}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Message area */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: '#f9fafb' }}>
        {!selectedThread ? (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>💬</div>
              <p>Sélectionnez une conversation ou créez-en une nouvelle</p>
            </div>
          </div>
        ) : (
          <>
            {/* Header with video call button */}
            <div style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', background: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <strong>{selectedThread.recipient_label || selectedThread.recipient_type || 'Conversation'}</strong>
                <span className={`badge ${selectedThread.status === 'open' ? 'blue' : 'gray'}`} style={{ marginLeft: 8, fontSize: 10 }}>
                  {selectedThread.status}
                </span>
              </div>
              <button
                className="btn primary"
                onClick={startVideoCall}
                style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}
              >
                📹 Appel vidéo
              </button>
            </div>

            {/* Messages */}
            <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
              {msgLoading ? <div style={{ textAlign: 'center', color: '#9ca3af' }}>Chargement...</div> :
               messages.length === 0 ? <div style={{ textAlign: 'center', color: '#9ca3af', marginTop: 40 }}>Aucun message. Envoyez le premier!</div> :
               messages.map(m => {
                const isVideoLink = m.content.includes('meet.jit.si/');
                return (
                  <div key={m.id} style={{
                    marginBottom: 12,
                    display: 'flex',
                    justifyContent: m.sender_type === 'hotel' ? 'flex-end' : 'flex-start',
                  }}>
                    <div style={{
                      maxWidth: '70%', padding: '10px 14px', borderRadius: 12,
                      background: isVideoLink ? '#f0fdf4' : m.sender_type === 'hotel' ? '#3b82f6' : 'white',
                      color: isVideoLink ? '#166534' : m.sender_type === 'hotel' ? 'white' : '#1f2937',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                      border: isVideoLink ? '1px solid #bbf7d0' : 'none',
                    }}>
                      {m.sender_type !== 'hotel' && (
                        <div style={{ fontSize: 10, color: '#6b7280', marginBottom: 4, textTransform: 'uppercase' }}>
                          {m.sender_type}
                        </div>
                      )}
                      <div style={{ fontSize: 14, whiteSpace: 'pre-wrap' }}>
                        {isVideoLink ? (
                          <>
                            📹 Vidéoconférence démarrée{' '}
                            <a
                              href={m.content.match(/https:\/\/meet\.jit\.si\/\S+/)?.[0] || '#'}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: '#2563eb', fontWeight: 600 }}
                            >
                              Rejoindre
                            </a>
                          </>
                        ) : m.content}
                      </div>
                      {m.has_attachments && (
                        <div style={{ fontSize: 11, marginTop: 4, opacity: 0.8 }}>📎 Pièce jointe</div>
                      )}
                      <div style={{ fontSize: 10, marginTop: 4, opacity: 0.7, textAlign: 'right' }}>
                        {new Date(m.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>

            {/* Input with attachment */}
            <div style={{ padding: '12px 16px', borderTop: '1px solid #e5e7eb', background: 'white' }}>
              {attachFile && (
                <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                  📎 {attachFile.name} ({(attachFile.size / 1024).toFixed(0)} Ko)
                  <button onClick={() => setAttachFile(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444' }}>✕</button>
                </div>
              )}
              <div style={{ display: 'flex', gap: 8 }}>
                <input type="file" ref={fileInputRef} style={{ display: 'none' }} onChange={e => setAttachFile(e.target.files?.[0] || null)} />
                <button
                  className="btn"
                  onClick={() => fileInputRef.current?.click()}
                  style={{ padding: '10px 12px', fontSize: 16 }}
                  title="Joindre un fichier"
                >
                  📎
                </button>
                <input
                  type="text"
                  value={newMsg}
                  onChange={e => setNewMsg(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleSend()}
                  placeholder="Tapez votre message..."
                  style={{ flex: 1, padding: '10px 14px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14 }}
                  disabled={sending}
                />
                <button className="btn primary" onClick={handleSend} disabled={sending || (!newMsg.trim() && !attachFile)}>
                  {sending ? '...' : 'Envoyer'}
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* New conversation dialog */}
      {showNewDialog && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }} onClick={() => setShowNewDialog(false)}>
          <div style={{ background: 'white', borderRadius: 12, padding: 24, width: 400, maxHeight: '80vh', overflowY: 'auto' }}
            onClick={e => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>Nouveau message</h3>
            <p style={{ color: '#6b7280', fontSize: 13 }}>Choisissez un correspondant institutionnel:</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 16 }}>
              {correspondents.length === 0 && (
                <div style={{ color: '#9ca3af', textAlign: 'center', padding: 20 }}>Aucun correspondant disponible</div>
              )}
              {correspondents.map((c: any) => (
                <button
                  key={c.id}
                  onClick={() => handleNewThread(c.id)}
                  style={{
                    padding: '12px 16px', borderRadius: 8, border: '1px solid #e5e7eb',
                    background: 'white', cursor: 'pointer', textAlign: 'left',
                  }}
                >
                  <strong style={{ fontSize: 14 }}>{c.label}</strong>
                  <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{c.description || c.agency}</div>
                </button>
              ))}
            </div>
            <button className="btn" style={{ width: '100%', marginTop: 16 }} onClick={() => setShowNewDialog(false)}>
              Annuler
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
